import { useState, useRef } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { createFileRoute, useNavigate, useBlocker, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { userQueries } from '@/lib/api/queries/user'
import { useCreateAgent } from '@/lib/api/mutations/agent'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog'
import { WizardProgress } from '@/components/create-agent/wizard-progress'
import { WizardFooter } from '@/components/create-agent/wizard-footer'
import { TemplateStep } from '@/components/create-agent/template-step'
import { ConfigurationStep } from '@/components/create-agent/configuration-step'
import { PromptStep } from '@/components/create-agent/prompt-step'
import { KnowledgeStep } from '@/components/create-agent/knowledge-step'
import { AccessStep } from '@/components/create-agent/access-step'
import type { CreateAgentPayload } from '@/lib/api/types'

export const Route = createFileRoute('/_authenticated/agents/create')({
  component: CreateAgentPage,
})

const createAgentSchema = z.object({
  templateKey: z.string().min(1, 'Please select a template'),
  name: z.string().min(1, 'Agent name is required').max(255, 'Agent name must be 255 characters or fewer'),
  description: z.string(),
  category: z.enum(['product', 'marketing', 'operations', 'general']),
  brandId: z.string().nullable(),
  promptConfig: z.object({
    system: z.string(),
    style: z.string(),
    guardrails: z.string(),
    outputFormat: z.string(),
  }),
})

type CreateAgentFormValues = z.infer<typeof createAgentSchema>

const STEP_FIELDS: Partial<Record<number, (keyof CreateAgentFormValues)[]>> = {
  1: ['templateKey'],
  2: ['name'],
}

function CreateAgentPage() {
  const [currentStep, setCurrentStep] = useState(1)
  const [highestVisitedStep, setHighestVisitedStep] = useState(1)
  const isSubmittedRef = useRef(false)

  const methods = useForm<CreateAgentFormValues>({
    resolver: zodResolver(createAgentSchema),
    defaultValues: {
      templateKey: '',
      name: '',
      description: '',
      category: 'general',
      brandId: null,
      promptConfig: { system: '', style: '', guardrails: '', outputFormat: '' },
    },
  })

  const { data: user } = useQuery(userQueries.me())
  const brands = user?.brands ?? []

  const createMutation = useCreateAgent()
  const navigate = useNavigate()

  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: () => {
      if (isSubmittedRef.current) return false
      const { name, description, promptConfig } = methods.getValues()
      const hasSubstantiveData =
        name.trim() !== '' ||
        (description ?? '').trim() !== '' ||
        Object.values(promptConfig).some((v) => v.trim() !== '')
      return hasSubstantiveData
    },
    withResolver: true,
    enableBeforeUnload: true,
  })

  const handleNext = async () => {
    const fields = STEP_FIELDS[currentStep]
    if (fields) {
      const isValid = await methods.trigger(fields)
      if (!isValid) return
    }
    const nextStep = Math.min(currentStep + 1, 5)
    setCurrentStep(nextStep)
    setHighestVisitedStep((prev) => Math.max(prev, nextStep))
  }

  const handleBack = () => {
    if (currentStep === 1) {
      navigate({ to: '/agents' })
    } else {
      setCurrentStep((s) => s - 1)
    }
  }

  const handleStepClick = (step: number) => {
    if (step < currentStep) {
      setCurrentStep(step)
    }
  }

  const handleSubmit = () => {
    const values = methods.getValues()
    const payload: CreateAgentPayload = {
      name: values.name,
      description: values.description || undefined,
      brandId: values.brandId || undefined,
      category: values.category,
      templateKey: values.templateKey,
      promptConfig: values.promptConfig,
    }
    createMutation.mutate(payload, {
      onSuccess: (data) => {
        isSubmittedRef.current = true
        navigate({ to: '/agents/$agentId', params: { agentId: data.id } })
      },
    })
  }

  const isNextDisabled = (() => {
    if (currentStep === 1) return !methods.watch('templateKey')
    return false
  })()

  return (
    <FormProvider {...methods}>
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="bg-card border-b border-border px-8 py-6">
          <Link
            to="/agents"
            className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Cancel
          </Link>
          <h1 className="text-4xl font-medium font-serif text-foreground">Create New Agent</h1>
          <p className="text-base text-muted-foreground mt-1">
            Follow the steps to configure your AI agent
          </p>
        </div>

        {/* Progress Bar */}
        <WizardProgress
          currentStep={currentStep}
          highestVisitedStep={highestVisitedStep}
          onStepClick={handleStepClick}
        />

        {/* Content Area */}
        <div className="flex-1 overflow-auto p-8">
          <div className="max-w-4xl mx-auto">
            {currentStep === 1 && <TemplateStep />}
            {currentStep === 2 && <ConfigurationStep brands={brands} />}
            {currentStep === 3 && <PromptStep />}
            {currentStep === 4 && <KnowledgeStep />}
            {currentStep === 5 && <AccessStep />}
          </div>
        </div>

        {/* Footer */}
        <WizardFooter
          currentStep={currentStep}
          onBack={handleBack}
          onNext={currentStep === 5 ? handleSubmit : handleNext}
          isNextDisabled={isNextDisabled}
          isSubmitting={createMutation.isPending}
        />
      </div>

      {/* Unsaved Changes Dialog (D-12) */}
      <AlertDialog open={status === 'blocked'}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Your progress will be lost if you leave.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={reset}>Stay</AlertDialogCancel>
            <AlertDialogAction onClick={proceed}>Leave</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FormProvider>
  )
}
